/*
 * 维护单组件（企微手机端 wechat_web）
 * 注册名：MwHeader（头部）/ MwMiddle（通用检查清单）/ MwMiddleDg（柴发月度维保检查清单）/ MwFooter（维护收尾）/ MwDgParallel（柴发并机测试，独立组件）
 * 引擎与样式与内网 PC 端（if-web）一致，此处只实现平台能力：
 *   打卡：复刻 components/Formily/Location.vue（签名 → wx.config → wx.getLocation，失败回退 H5 定位 → 腾讯地图逆地址 → addSignUp）
 *   扫码：复刻 components/Formily/Scanner.vue（wx.scanQRCode）
 */
import axios from "axios";
import dayjs from "dayjs";
import store from "@/store";
import TMap from "@/api/MapApi";
import { addSignUp } from "@/api/signUp/signUpApi";
import createPart from "./createPart";

const NONCE = "otob";

function cfg(key) {
  try {
    return store.getters.getConfigItem(key);
  } catch (e) {
    return "";
  }
}

function authHeaders() {
  return {
    authToken: sessionStorage.getItem("AuthToken") || "",
    userid: sessionStorage.getItem("userid") || "",
  };
}

function report(data) {
  try {
    const aegis = store.state.user && store.state.user.aegis;
    aegis && aegis.report && aegis.report({ msg: "[comp-maintain] " + JSON.stringify(data) });
  } catch (e) {}
}

/** 获取企微 JS-SDK 签名并完成 wx.config，返回 ready 后的 wx */
function wxReady(apiList) {
  const wx = window.wx;
  if (!wx || !wx.config) return Promise.reject(new Error("no_wx"));
  const Timestamp = `${Math.round(Date.now() / 1000)}`;
  return axios
    .post(cfg("signature"), { Url: window.location.href, Noncestr: NONCE, Timestamp }, { headers: authHeaders() })
    .then((res) => {
      const signature = res && res.data && res.data.signature;
      if (!signature) throw new Error("获取企微签名失败");
      return new Promise((resolve, reject) => {
        wx.config({
          beta: true,
          debug: false,
          appId: sessionStorage.getItem("appid") || cfg("corpID"),
          timestamp: Timestamp * 1,
          nonceStr: NONCE,
          signature,
          jsApiList: apiList,
        });
        wx.error((e) => reject(new Error("企微 SDK 初始化失败：" + JSON.stringify(e))));
        wx.ready(() => resolve(wx));
      });
    });
}

function h5Position() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("获取地址失败，请确认打开定位服务后再试"));
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      (e) => reject(new Error("H5 获取地址失败：" + ((e && (e.message || e.code)) || "")))
    );
  });
}

function wxPosition() {
  return wxReady(["getLocation"]).then(
    (wx) =>
      new Promise((resolve, reject) => {
        wx.getLocation({
          type: "gcj02",
          success: (res) => resolve({ latitude: res.latitude, longitude: res.longitude }),
          cancel: () => reject(Object.assign(new Error("打卡已取消"), { cancel: true })),
          fail: (res) => {
            report({ action: "wx_get_location_failed", error: JSON.stringify(res || {}) });
            h5Position().then(resolve, reject);
          },
        });
      })
  );
}

function position() {
  return wxPosition().catch((e) => {
    if (e && e.cancel) throw e;
    report({ action: "wx_location_fallback", error: (e && e.message) || String(e) });
    return h5Position();
  });
}

async function geocode(lat, lng) {
  const map = await TMap.init();
  if (!map) throw new Error("腾讯地图 SDK 启动失败，请稍后重试");
  const coder = new map.service.Geocoder();
  const res = await coder.getAddress({ location: new map.LatLng(lat, lng) });
  const r = (res && res.result) || {};
  const fa = r.formatted_addresses || {};
  return fa.recommend || fa.rough || r.address || "";
}

const wechatPlatform = {
  /** 企微端 Nbroker / Ticket 接口走 /relay/ifob/api（与 formilyTodoForm.formatSchema 中对 schema 的改写保持一致） */
  apiUrl: "/relay/ifob/api",
  /** 企微端页面整体滚动 */
  scrollEl() {
    return null;
  },
  isMobileUA() {
    return true;
  },
  /** 打卡：返回值结构与 Location 组件 change 事件一致 */
  async locate({ user, taskId, ticketId }) {
    const { latitude, longitude } = await position();
    const address = await geocode(latitude, longitude);
    const params = {
      Address: address,
      Latitude: latitude,
      Longitude: longitude,
      SignUser: user || sessionStorage.getItem("userid") || "",
      TaskId: taskId || "0",
      TicketId: ticketId || "0",
    };
    const res = await addSignUp(params);
    if (!res || !res.LastId) throw new Error("打卡接口未返回记录");
    return {
      ...params,
      SignDesc: "打卡",
      Id: res.LastId,
      SignTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };
  },
  /** 扫码：返回扫描结果字符串 */
  scan() {
    return wxReady(["scanQRCode"]).then(
      (wx) =>
        new Promise((resolve, reject) => {
          wx.scanQRCode({
            desc: "scanQRCode desc",
            needResult: 1,
            scanType: ["qrCode", "barCode"],
            success: (res) => resolve((res && res.resultStr) || ""),
            cancel: () => resolve(""),
            fail: (res) => reject(new Error((res && res.errMsg) || "扫码失败")),
            error: (res) => {
              const msg = (res && res.errMsg) || "扫码失败";
              reject(new Error(msg.indexOf("function_not_exist") > -1 ? "企业微信版本过低，请升级" : msg));
            },
          });
        })
    );
  },
  uploadHeaders() {
    return authHeaders();
  },
};

export const MwHeader = createPart("header", "MwHeader", wechatPlatform);
export const MwMiddle = createPart("middle", "MwMiddle", wechatPlatform);
export const MwMiddleDg = createPart("middle_dg", "MwMiddleDg", wechatPlatform);
export const MwFooter = createPart("footer", "MwFooter", wechatPlatform);
export const MwDgParallel = createPart("parallel_dg", "MwDgParallel", wechatPlatform);

export default { MwHeader, MwMiddle, MwMiddleDg, MwFooter, MwDgParallel };
